import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Grid, Typography } from "@sumprio/ui";
import { reportClientError } from "helpers";

export interface SectionErrorBoundaryProps {
  children: ReactNode;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportClientError({ boundary: "SectionErrorBoundary", error, errorInfo });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Grid
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="md"
          padding="2xl"
          flex={1}
          width="100%"
          style={{ minHeight: 280 }}
        >
          <Typography variant="body" bodySize="md" bodyWeight="medium">
            Something went wrong in this area.
          </Typography>
          <Typography variant="body" bodySize="sm" bodyWeight="regular" color="secondary">
            You can try again or refresh the page. If the problem continues, contact support.
          </Typography>
          {this.props.onRetry && (
            <Button variant="brand-secondaryGrey" size="sm" onClick={this.handleRetry}>
              Try again
            </Button>
          )}
        </Grid>
      );
    }
    return this.props.children;
  }
}
